import { useState, type ReactNode } from 'react';
import {
  Archive,
  ArrowRightLeft,
  ArrowUpCircle,
  Copy,
  EyeOff,
  Import,
  Languages,
  Layers,
  LifeBuoy,
  Play,
  RotateCw,
  Trash2,
} from 'lucide-react';
import { CaptureTemplateDialog } from '@/components/geo/CaptureTemplateDialog';
import { StructureSheet } from '@/components/geo/StructureSheet';
import { Button, ConfirmDialog, MenuButton, type MenuItem } from '@/components/ui';
import { CloneSiteDrawer } from '@/components/sites/CloneSiteDrawer';
import { DestroySiteDialog } from '@/components/sites/DestroySiteDialog';
import { ImportSiteDrawer } from '@/components/sites/ImportSiteDrawer';
import { MigrateSiteDialog } from '@/components/sites/MigrateSiteDialog';
import { RelocateSiteDialog } from '@/components/sites/RelocateSiteDialog';
import { UnobserveSiteDialog } from '@/components/sites/UnobserveSiteDialog';
import { UpgradeSiteDialog } from '@/components/sites/UpgradeSiteDialog';
import { idempotencyKey } from '@/utils/idempotency';
import { useSiteOperation } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { OPERATION_LABELS } from '@/utils/labels';
import type { SiteCapabilities } from '@/utils/siteCapabilities';
import type { SiteDetail } from '@/types/api';

/** Operations confirmed with one click in a dialog; each queues a job of the same name. */
const SIMPLE = {
  restart: {
    title: 'Перезапустить сайт?',
    description: 'Сайт будет недоступен несколько секунд.',
    confirmLabel: 'Перезапустить',
  },
  deploy: {
    title: 'Развернуть сайт заново?',
    description: 'Сайт будет развёрнут по текущей конфигурации.',
    confirmLabel: 'Развернуть',
  },
  backup: {
    title: 'Создать резервную копию?',
    description: 'Копия появится на вкладке «Копии».',
    confirmLabel: 'Создать копию',
  },
} as const;

type Simple = keyof typeof SIMPLE;
type Opened = Simple | 'destroy' | 'clone' | 'migrate' | 'upgrade' | 'relocate' | 'unobserve' | 'import' | 'template' | 'translate';

/**
 * Site operations, as the buttons in the site page's header: the everyday
 * ones in sight, the rarer and the destructive under «Ещё».
 *
 * Every one of these queues a job rather than doing the work, so the feedback
 * is "поставлено в очередь" and the History tab is where the outcome appears.
 * Saying "готово" here would be a lie about work that has not started. Which
 * buttons show is `siteCapabilities`' decision.
 */
export function SiteActions({ site, may }: { readonly site: SiteDetail; readonly may: SiteCapabilities }): ReactNode {
  const [opened, setOpened] = useState<Opened | null>(null);
  const close = (): void => setOpened(null);

  const run = useSiteOperation(site.id, {
    onSuccess: (_result, { command }) => {
      close();
      toast.success('Операция поставлена в очередь', OPERATION_LABELS[command.operation]);
    },
  });

  const simple = opened && opened in SIMPLE ? (opened as Simple) : null;
  const confirmSimple = simple ? (
    <ConfirmDialog
      open
      title={SIMPLE[simple].title}
      description={SIMPLE[simple].description}
      confirmLabel={SIMPLE[simple].confirmLabel}
      busy={run.isPending}
      error={run.isError ? errorText(run.error, 'Не удалось поставить операцию в очередь') : undefined}
      onConfirm={() => run.mutate({ command: { operation: simple }, key: idempotencyKey(`site.${simple}`) })}
      onClose={close}
    />
  ) : null;

  const more = (items: readonly (MenuItem | false)[]) => <MenuButton size="sm" label="Ещё" items={items.filter((item): item is MenuItem => item !== false)} />;

  if (may.observed) {
    return (
      <div className="row-tight">
        {may.backup ? (
          <Button size="sm" icon={Archive} onClick={() => setOpened('backup')}>
            Создать копию
          </Button>
        ) : null}
        {may.clone ? (
          <Button size="sm" icon={Copy} onClick={() => setOpened('clone')}>
            Клонировать
          </Button>
        ) : null}
        {may.importWordpress ? (
          <Button size="sm" variant="primary" icon={Import} onClick={() => setOpened('import')}>
            Импорт в панель
          </Button>
        ) : null}
        {more([may.importObserved && { label: 'Убрать из мониторинга', icon: EyeOff, onSelect: () => setOpened('unobserve') }])}
        <UnobserveSiteDialog site={site} open={opened === 'unobserve'} onClose={close} />
        {may.clone ? <CloneSiteDrawer site={site} open={opened === 'clone'} onClose={close} /> : null}
        {opened === 'import' ? <ImportSiteDrawer open onClose={close} from={site} /> : null}
        {confirmSimple}
      </div>
    );
  }

  return (
    <>
      {may.relocate ? (
        <Button size="sm" variant="primary" icon={LifeBuoy} onClick={() => setOpened('relocate')}>
          Восстановить на другом сервере
        </Button>
      ) : null}

      {may.deploy ? (
        <Button size="sm" icon={RotateCw} onClick={() => setOpened('restart')}>
          Перезапустить
        </Button>
      ) : null}

      {may.backup ? (
        <Button size="sm" icon={Archive} onClick={() => setOpened('backup')}>
          Создать копию
        </Button>
      ) : null}

      {may.clone ? (
        <Button size="sm" icon={Copy} onClick={() => setOpened('clone')}>
          Клонировать
        </Button>
      ) : null}

      {more([
        may.translate && { label: 'Перевод структуры', icon: Languages, onSelect: () => setOpened('translate') },
        may.captureTemplate && {
          label: site.referenceOf ? 'Опубликовать версию шаблона' : 'Сделать шаблоном',
          icon: Layers,
          onSelect: () => setOpened('template'),
        },
        may.deploy && { label: 'Развернуть заново', icon: Play, onSelect: () => setOpened('deploy') },
        may.upgrade && { label: 'Обновить WordPress/PHP', icon: ArrowUpCircle, onSelect: () => setOpened('upgrade') },
        may.migrate && { label: 'Перенести на другой сервер', icon: ArrowRightLeft, onSelect: () => setOpened('migrate') },
        may.destroy && { label: 'Удалить сайт', icon: Trash2, onSelect: () => setOpened('destroy'), tone: 'danger' },
      ])}

      {may.clone ? <CloneSiteDrawer site={site} open={opened === 'clone'} onClose={close} /> : null}
      {may.migrate ? <MigrateSiteDialog site={site} open={opened === 'migrate'} onClose={close} /> : null}
      {may.upgrade ? <UpgradeSiteDialog site={site} open={opened === 'upgrade'} onClose={close} /> : null}
      {may.relocate ? <RelocateSiteDialog site={site} open={opened === 'relocate'} onClose={close} /> : null}
      {may.captureTemplate ? <CaptureTemplateDialog site={site} open={opened === 'template'} onClose={close} /> : null}
      {may.translate ? <StructureSheet siteId={site.id} open={opened === 'translate'} onClose={close} /> : null}

      {confirmSimple}

      <DestroySiteDialog site={site} open={opened === 'destroy'} onClose={close} />
    </>
  );
}
